import { uid } from '../../../../lib/uid';
import type { SurveyQuestion } from '../../survey/surveyTypes';
import type { PersonnelCatalogEntry } from '../personnelTypes';

/** Plausible states a case can be in, as an employee would write them. */
const CASE_STATUSES = ['Pending before the trial court', 'Dismissed', 'Under preliminary investigation'];

/**
 * A sample answer to ONE survey question, so the form fills in one click.
 *
 * **It takes the chosen question ROW and reads the flags off it**, exactly as
 * `sampleFamilyMember` takes the relation. A generator that special-cased the twelve seeded item
 * numbers would fill nothing for a question an employer added and silently produce a form that
 * cannot be saved — which is the one thing Autogenerate exists to prevent.
 *
 * **It answers YES**, deterministically. That is the branch that draws every follow-up the
 * question declares, so the screens behind the form get exercised; a generated "No" would fill
 * one radio and leave the rest of the form untouched. It is also predictable, which a generator
 * has to be.
 *
 * **The details are obviously synthetic.** Item 34 asks who an employee is related to, and a
 * fabricated-but-realistic person in that box reads as a fact about somebody. The value lands in
 * `rsys_` rather than the identity store, so the rule does not forbid it — it is simply not
 * worth writing a name nobody can tell apart from a real one.
 */
export function sampleSurveyAnswer(
  question: SurveyQuestion,
  countries: PersonnelCatalogEntry[],
): {
  surveyQuestionId: string;
  answer: 'yes';
  details: string;
  dateFiled: string;
  caseStatus: string;
  idNumber: string;
  countryId: string | null;
} {
  const tag = uid().slice(0, 6).toUpperCase();

  // A real row from the catalog the form already loaded: an id the generator invented would
  // fail the server's "it must exist and be active" check, which reads as a bug in the form.
  const country = countries.length > 0 ? countries[Math.floor(Math.random() * countries.length)] : null;

  // Two years back, so it is comfortably in the past whatever day this runs — the server caps
  // a filing date at today.
  const filed = new Date();
  filed.setFullYear(filed.getFullYear() - 2);

  return {
    surveyQuestionId: question.id,
    answer: 'yes',
    details: question.collectsDetails
      ? `Sample answer ${tag}. Generated for testing — replace before this record is used.`
      : '',
    dateFiled: question.collectsDate ? filed.toISOString().slice(0, 10) : '',
    caseStatus: question.collectsCaseStatus
      ? CASE_STATUSES[Math.floor(Math.random() * CASE_STATUSES.length)]
      : '',
    idNumber: question.collectsIdNumber ? `TEST-${tag}` : '',
    countryId: question.collectsCountry ? (country?.id ?? null) : null,
  };
}
